"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const React = require("react");
const LoggerHelper_1 = require("src/ts/helpers/LoggerHelper");
const SharedTypes_1 = require("src/ts/models/SharedTypes");
const StackNavigationContext_1 = require("../../contexts/StackNavigationContext");
const LoadingView_1 = require("./LoadingView");
class StackNavigationView extends React.PureComponent {
    constructor(props) {
        super(props);
        this._track = (type) => {
            const views = this.state.views;
            if (!views.length) {
                return;
            }
            const current = views[views.length - 1];
            if (current.properties.eventViewName) {
                LoggerHelper_1.logger.navigation({ view: current.properties.eventViewName, type, isRoot: !this.props.parentContext }, current.properties.eventPriority);
            }
        };
        this._push = (view, properties) => {
            if (properties && properties.ariaMessage) {
                this.props.ariaContext.announce(properties.ariaMessage);
            }
            this.setState((prevState) => {
                if (prevState.animating) {
                    return null;
                }
                const views = [...prevState.views];
                views.push({
                    view,
                    properties: { ...StackNavigationContext_1.defaultStackViewProperties, ...properties },
                });
                let animating = false;
                if (properties && properties.animation && properties.animation.type !== StackNavigationContext_1.AnimationType.None) {
                    animating = true;
                    setTimeout(() => {
                        this.setState({ animating: false });
                    }, properties.animation.duration * 1000);
                }
                return {
                    views,
                    animating,
                };
            }, () => this._track('push'));
        };
        this._pop = () => {
            this._track('pop');
            if (this.state.views.length > 1) {
                const previous = this.state.views[this.state.views.length - 2];
                if (previous.properties.ariaMessage) {
                    this.props.ariaContext.announce(previous.properties.ariaMessage);
                }
            }
            this.setState((prevState, props) => {
                if (prevState.animating) {
                    return null;
                }
                const views = [...prevState.views];
                if (!views.length) {
                    return null;
                }
                let animating = false;
                const current = views[views.length - 1];
                const usedAnimation = current.properties.animation;
                if (current.properties.eventViewName) {
                    LoggerHelper_1.logger.navigation({ view: current.properties.eventViewName, type: 'pop', isRoot: !props.parentContext });
                }
                if (usedAnimation.type === StackNavigationContext_1.AnimationType.None) {
                    views.pop();
                }
                else {
                    animating = true;
                    current.properties.animation = { ...usedAnimation, type: `${usedAnimation.type}Out` };
                    setTimeout(() => {
                        animating = false;
                        views.pop();
                        this.setState({
                            views,
                            animating,
                        });
                    }, current.properties.animation.duration * 1000);
                }
                return {
                    views,
                    animating,
                };
            });
        };
        this._getAnimationStyle = (animation, prev) => {
            switch (animation.type) {
                case StackNavigationContext_1.AnimationType.Fade:
                    return prev ?
                        { animation: `stackFadeOut ${animation.duration}s forwards` }
                        : { animation: `stackFade ${animation.duration}s forwards` };
                case StackNavigationContext_1.AnimationType.FadeOut:
                    return prev ?
                        { animation: `stackFade ${animation.duration}s forwards` }
                        : { animation: `stackFadeOut ${animation.duration}s forwards` };
                case StackNavigationContext_1.AnimationType.Slide:
                    return prev ?
                        { animation: `stackFadeOut ${animation.duration}s forwards, stackRightOut ${animation.duration}s forwards` }
                        : { animation: `stackFade ${animation.duration}s forwards, stackLeft ${animation.duration}s forwards` };
                case StackNavigationContext_1.AnimationType.SlideOut:
                    return prev ?
                        { animation: `stackFade ${animation.duration}s forwards, stackRight ${animation.duration}s forwards` }
                        : { animation: `stackFadeOut ${animation.duration}s forwards, stackLeftOut ${animation.duration}s forwards` };
                case StackNavigationContext_1.AnimationType.None:
                    return {};
                default:
                    throw new Error('Not implemented yet');
            }
        };
        this.renderPrevView = (current, prev, visible = true) => {
            const fullscreenClassName = !this.props.parentContext ? 'fullscreen' : 'nested';
            return React.createElement("div", { key: 'previous', className: `stack prev ${fullscreenClassName}`, style: { ...this._getAnimationStyle(current.properties.animation, true), visibility: visible ? undefined : 'hidden' } }, prev.view);
        };
        this.renderCurrentView = (current) => {
            const fullscreenClassName = !this.props.parentContext ? 'fullscreen' : 'nested';
            return React.createElement("div", { key: 'current', id: this.state.mainContentId, role: 'main', className: `stack current ${fullscreenClassName}`, style: this._getAnimationStyle(current.properties.animation, false) }, current.view);
        };
        this.renderViews = () => {
            if (!this.state.views.length) {
                return undefined;
            }
            const current = this.state.views[this.state.views.length - 1];
            let prev;
            for (let i = this.state.views.length - 1; i > 0; i--) {
                const candidate = this.state.views[i - 1];
                if (candidate.properties.type !== StackNavigationContext_1.ViewType.Overlay) {
                    prev = candidate;
                    break;
                }
            }
            if (prev && this.state.animating) {
                return React.createElement(React.Fragment, null,
                    this.renderPrevView(current, prev),
                    this.renderCurrentView(current));
            }
            else if (prev && current.properties.type === StackNavigationContext_1.ViewType.Overlay) {
                // Modal should always be displayed on top of a screen, in order to keep the state and have a proper close animation
                // Visibility hidden prevent user from tabbing inside the 'prev' view
                return React.createElement(React.Fragment, null,
                    this.renderPrevView(current, prev, false),
                    this.renderCurrentView(current));
            }
            return React.createElement(React.Fragment, null, this.renderCurrentView(current));
        };
        this.state = {
            animating: false,
            views: [{ view: props.initialView, properties: { ...StackNavigationContext_1.defaultStackViewProperties, ...props.initialViewProps } }],
            push: this._push,
            pop: this._pop,
            pushRoot: props.parentContext ? props.parentContext.pushRoot : this._push,
            popRoot: props.parentContext ? props.parentContext.popRoot : this._pop,
            pushParent: props.parentContext ? props.parentContext.push : this._push,
            popParent: props.parentContext ? props.parentContext.pop : this._pop,
            mainContentId: props.parentContext ? undefined : SharedTypes_1.mainContentId,
        };
    }
    componentWillMount() {
        this._track('push');
    }
    componentWillUnmount() {
        this._track('pop');
    }
    render() {
        return React.createElement(StackNavigationContext_1.StackNavigationContextProvider, { value: this.state }, this.props.loading ? React.createElement(LoadingView_1.LoadingView, null) : this.renderViews());
    }
}
exports.StackNavigationView = StackNavigationView;
